import { SidePanel } from '@hue/design-system';
import { ArrowDown } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  documentationPageHelp,
  type DocumentationEffectTone,
  type DocumentationFlow,
  type DocumentationFlowStep,
  type DocumentationPageKey,
} from './system-documentation-flows.js';

const TONE: Record<DocumentationEffectTone, string> = {
  info: 'border-info/35 bg-info/10 text-info',
  positive: 'border-positive/35 bg-positive/10 text-positive',
  warning: 'border-warning/40 bg-warning/10 text-warning',
};

interface Edge {
  d: string;
  optional: boolean;
  label?: { x: number; y: number; text: string } | undefined;
}

/// O fluxo desenhado: cada coluna é uma etapa, e as caixas da mesma coluna são caminhos
/// alternativos. No computador as setas ligam as caixas; no celular o desenho vira uma lista na
/// ordem das etapas, com a seta entre elas, porque setas de lado não cabem na largura do polegar.
export function DocumentationFlowDiagram({
  flow,
  from,
  highlighted,
  renderOpen,
}: {
  flow: DocumentationFlow;
  from?: DocumentationPageKey | undefined;
  highlighted: (key: string, text: string) => ReactNode;
  renderOpen?: ((page: DocumentationPageKey, label: string) => ReactNode) | undefined;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [open, setOpen] = useState<DocumentationFlowStep>();
  const columns = [...new Set(flow.steps.map((step) => step.column))].sort((a, b) => a - b);
  const rows = Math.max(...flow.steps.map((step) => step.row));

  const draw = useCallback(() => {
    const box = container.current!;
    if (getComputedStyle(box).display !== 'grid') {
      setEdges([]);
      return;
    }
    const origin = box.getBoundingClientRect();
    const place = (id: string) => {
      const rect = box.querySelector(`[data-flow-step="${id}"]`)!.getBoundingClientRect();
      return {
        left: rect.left - origin.left,
        right: rect.right - origin.left,
        middle: rect.top - origin.top + Math.min(32, rect.height / 2),
      };
    };
    setEdges(
      flow.links.map((link) => {
        const source = place(link.from);
        const target = place(link.to);
        const x1 = source.right;
        const x2 = target.left - 3;
        const bend = Math.max(16, (x2 - x1) / 2);
        return {
          d: `M${x1} ${source.middle} C${x1 + bend} ${source.middle}, ${x2 - bend} ${target.middle}, ${x2} ${target.middle}`,
          optional: link.optional === true,
          label: link.label
            ? { x: (x1 + x2) / 2, y: (source.middle + target.middle) / 2 - 6, text: link.label }
            : undefined,
        };
      }),
    );
  }, [flow.links]);

  useLayoutEffect(() => {
    draw();
    window.addEventListener('resize', draw);
    const observer = new ResizeObserver(() => draw());
    observer.observe(container.current!);
    return () => {
      window.removeEventListener('resize', draw);
      observer.disconnect();
    };
  }, [draw]);

  const marker = `flow-arrow-${flow.key}`;
  return (
    <>
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-content-muted">
        <span className="inline-flex items-center gap-2">
          <i className="h-3 w-5 rounded border-[1.5px] border-surface-border bg-surface-card" />
          Passo do caminho
        </span>
        <span className="inline-flex items-center gap-2">
          <i className="h-3 w-5 rounded border-[1.5px] border-dashed border-surface-border bg-surface-card" />
          Opcional
        </span>
        <span className="inline-flex items-center gap-2">
          <i className="h-3 w-5 rounded-full border border-info/40 bg-info/15" />O sistema faz
          sozinho
        </span>
        {from ? (
          <span className="inline-flex items-center gap-2">
            <i className="h-3 w-5 rounded border-[1.5px] border-brand-500 ring-2 ring-brand-500/25" />
            Feito na página de onde você veio
          </span>
        ) : null}
        <span>Clique numa caixa para ver o passo a passo.</span>
      </div>

      <div className="mt-4 overflow-x-auto pb-2">
        <div
          ref={container}
          className="relative flex flex-col gap-2 md:grid md:min-w-max md:gap-x-12 md:gap-y-3 md:p-0.5"
          style={{
            gridTemplateColumns: `repeat(${columns.length}, auto)`,
            gridTemplateRows: `auto repeat(${rows}, auto)`,
          }}
        >
          {edges.length ? (
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 size-full overflow-visible"
            >
              <defs>
                <marker
                  id={marker}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path d="M0 0L10 5L0 10z" className="fill-content-muted" />
                </marker>
              </defs>
              {edges.map((edge, index) => (
                <g key={index}>
                  <path
                    d={edge.d}
                    markerEnd={`url(#${marker})`}
                    className="fill-none stroke-content-muted/70"
                    strokeWidth={1.6}
                    strokeDasharray={edge.optional ? '5 4' : undefined}
                  />
                  {edge.label ? (
                    <text
                      x={edge.label.x}
                      y={edge.label.y}
                      textAnchor="middle"
                      className="fill-content-muted stroke-surface text-[10.5px] font-bold [paint-order:stroke]"
                      strokeWidth={4}
                    >
                      {edge.label.text}
                    </text>
                  ) : null}
                </g>
              ))}
            </svg>
          ) : null}
          {columns.map((column, index) => (
            <p
              key={`stage-${column}`}
              className="mt-3 flex items-center gap-1.5 self-end text-[11px] font-bold uppercase tracking-[.06em] text-content-muted first:mt-0 md:mt-0"
              style={{ gridColumn: index + 1, gridRow: 1, order: column * 10 }}
            >
              {index > 0 ? (
                <ArrowDown className="size-3.5 text-brand-400 md:hidden" aria-hidden="true" />
              ) : null}
              {index + 1}. {highlighted(`flow-${flow.key}-stage-${index}`, flow.stages[index]!)}
            </p>
          ))}
          {flow.steps.map((step) => {
            const here = from !== undefined && step.pages.includes(from);
            const prefix = `flow-${flow.key}-${step.id}`;
            return (
              <button
                key={step.id}
                type="button"
                data-flow-step={step.id}
                onClick={() => setOpen(step)}
                aria-label={`${step.title}: ver o passo a passo`}
                className={`relative z-[1] grid content-start gap-1.5 self-start rounded-2xl border-[1.5px] bg-surface-card px-3 py-2.5 text-left transition hover:border-brand-500/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 md:w-56 ${
                  step.optional ? 'border-dashed' : ''
                } ${here ? 'border-brand-500 ring-[3px] ring-brand-500/25' : 'border-surface-border'}`}
                style={{
                  gridColumn: columns.indexOf(step.column) + 1,
                  gridRow: step.row + 1,
                  order: step.column * 10 + step.row,
                }}
              >
                {here ? (
                  <span className="justify-self-start rounded-full bg-brand-500 px-2 text-[10px] font-bold uppercase tracking-[.05em] text-on-brand">
                    Você está aqui
                  </span>
                ) : step.optional ? (
                  <span className="justify-self-start rounded-full bg-surface-elevated px-2 text-[10px] font-bold uppercase tracking-[.05em] text-content-muted">
                    Opcional
                  </span>
                ) : null}
                <span className="text-[13.5px] font-bold leading-snug text-content">
                  {highlighted(`${prefix}-title`, step.title)}
                </span>
                <span className="text-[11.5px] font-semibold text-brand-400">
                  {highlighted(`${prefix}-where`, step.where)}
                </span>
                <span className="text-xs leading-5 text-content-secondary">
                  {highlighted(`${prefix}-summary`, step.summary)}
                </span>
                {step.effects?.length ? (
                  <span className="flex flex-wrap gap-1">
                    {step.effects.map((effect, index) => (
                      <span
                        key={effect.text}
                        className={`rounded-full border px-2 text-[10.5px] font-semibold ${TONE[effect.tone]}`}
                      >
                        {highlighted(`${prefix}-effect-${index}`, effect.text)}
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <SidePanel
        open={open !== undefined}
        onOpenChange={(next) => {
          if (!next) setOpen(undefined);
        }}
        title={open?.title ?? ''}
        description={open?.where}
        footer={
          open?.opens && renderOpen
            ? renderOpen(open.opens, `Abrir ${documentationPageHelp[open.opens].name}`)
            : undefined
        }
      >
        {open ? <StepDetail flowTitle={flow.title} step={open} /> : null}
      </SidePanel>
    </>
  );
}

function StepDetail({ flowTitle, step }: { flowTitle: string; step: DocumentationFlowStep }) {
  return (
    <div className="grid gap-6 text-sm leading-6">
      <p className="text-[11px] font-bold uppercase tracking-[.08em] text-brand-400">{flowTitle}</p>
      <section>
        <h3 className="text-xs font-bold uppercase tracking-[.06em] text-content-muted">
          Passo a passo
        </h3>
        <ol className="mt-2 grid list-decimal gap-2 pl-5 text-content-secondary">
          {step.steps.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ol>
      </section>
      {step.effects?.length ? (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[.06em] text-content-muted">
            O que o sistema faz sozinho
          </h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {step.effects.map((effect) => (
              <span
                key={effect.text}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE[effect.tone]}`}
              >
                {effect.text}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {step.notes?.length ? (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[.06em] text-content-muted">
            Bom saber
          </h3>
          <ul className="mt-2 grid list-disc gap-2 pl-5 text-content-secondary">
            {step.notes.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
